import { CartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import { MeQueryResult } from '@graphql/queries/__generated__/me.generated'
import { Divider } from '@mui/material'
import Button from '@mui/material/Button'
import React, { useMemo } from 'react'
import Loader from '@components/Loader'
import { Link } from 'react-router-dom'
import { CHECKOUT_URL } from '../../../urls'
import { AfterPayInfo } from '@components/AfterPayInfo'
import FormattedPrice from '@components/FormattedPrice'
import { useTranslation } from 'react-i18next'
import CartShippingDiscount from './CartShippingDiscount'
import CartSummary from '@components/CartSummary'
import { Rewards } from '@components/Rewards'
import { useReactiveVar } from '@apollo/client'
import { headerOffsetAnimatedVar, headerOffsetVar } from '../../../../apolloClient'
import { STICKY_PANEL_GAP } from '../../../../constants'

interface CategoryCartLine {
  pk: number
  pcs: number
  qty: number
  price: number
  weight: number
  category: string
  sortOrder: number
  currency: string
}

interface CartLinesTotalProps {
  handleSignIn(): void

  isAuthenticated: boolean
  loading: boolean

  cartQueryResult: CartQueryResult
  meQueryResult: MeQueryResult
}

const CartLinesTotal: React.FC<CartLinesTotalProps> = ({
  handleSignIn,
  isAuthenticated,
  cartQueryResult,
  loading,
  meQueryResult,
}) => {
  const { t } = useTranslation()
  const headerOffset = useReactiveVar(headerOffsetVar)
  const headerOffsetAnimated = useReactiveVar(headerOffsetAnimatedVar)
  const itemSubtotal = cartQueryResult.data?.cart.totalPrice.discount.gross
  const rewards = meQueryResult.data?.me?.totalBalance?.gross || 0
  const creditsApplied = rewards > itemSubtotal ? itemSubtotal : rewards
  const earnPoints = useMemo(
    () =>
      cartQueryResult.data?.cart
        ? Math.round((cartQueryResult.data?.cart?.totalPrice.earnPoints - creditsApplied) * 100) / 100
        : 0,
    [cartQueryResult.data, creditsApplied]
  )

  const currency = cartQueryResult.data?.cart?.totalPrice.currency || 'USD'

  const categoriesWithLines: {
    [key: number]: CategoryCartLine[]
  } = cartQueryResult.data?.cart?.linesByCategory ? cartQueryResult.data.cart.linesByCategory : {}

  return (
    <>
      {!!cartQueryResult.data?.cart?.count && (
        <div
          className='bg-white shadow-card md:sticky rounded-lg overflow-hidden z-10 mb-20'
          style={{
            top: headerOffset + STICKY_PANEL_GAP,
            transition: headerOffsetAnimated ? 'top 0.2s' : 'none',
          }}
        >
          <p className='text-bold text-lg p-20 mb-0 mt-0 leading-tight capitalize'>
            {t('orderSummary', 'Order Summary')}
          </p>
          <Divider />
          <div className='p-20 text-13'>
            {!!Object.keys(categoriesWithLines).length && <CartSummary categoriesWithLines={categoriesWithLines} />}
          </div>
          {!!creditsApplied && (
            <>
              <Divider />
              <div className='p-20 text-13'>
                <div className='flex items-center justify-between'>
                  <div>{t('creditsApplied', 'Credits applied')}</div>
                  <span className={loading ? 'opacity-0' : ''}>
                    <FormattedPrice currency={currency} amount={+creditsApplied} />
                  </span>
                </div>
              </div>
            </>
          )}
          <Divider />
          <div className='p-20 flex items-center justify-between text-2xl font-bold'>
            <span>{t('estimatedTotal', 'Estimated Total')}</span>
            <span className={`${loading ? 'opacity-0' : ''}`}>
              <FormattedPrice currency={currency} amount={itemSubtotal - creditsApplied} />
            </span>
          </div>
          <Divider />
          {/*<div className='text-gray text-sm flex justify-center my-10'>*/}
          {/*  <AfterPayInfo*/}
          {/*    minPrice={itemSubtotal - creditsApplied}*/}
          {/*    maxPrice={itemSubtotal - creditsApplied}*/}
          {/*    currency={currency}*/}
          {/*  />*/}
          {/*</div>*/}
          <div className='p-20 pb-10 text-13'>
            {t('shippingAndTaxCalculatedInCheckout', 'Shipping cost and Tax calculated in Checkout')}
          </div>

          <div className='px-20 pb-20'>
            <Button
              component={Link}
              to={CHECKOUT_URL}
              color='secondary'
              variant='contained'
              size='large'
              disableElevation
              fullWidth
            >
              <span className='font-bold'>{t('button.proceedToCheckout', 'Proceed to Checkout')}</span>
            </Button>
          </div>
        </div>
      )}
      <div className='mb-20 md:mb-0 bg-white shadow-card rounded-lg overflow-hidden'>
        <CartShippingDiscount />
      </div>
      <div className='mb-20 md:mb-0 md:mt-20 bg-white shadow-card rounded-lg overflow-hidden'>
        <Rewards
          earnPoints={earnPoints}
          isAuthenticated={isAuthenticated}
          loading={loading}
          handleSignIn={handleSignIn}
        />
      </div>
      {!!cartQueryResult.data?.cart?.count && (
        <div
          className='fixed block md:hidden bottom-0 bg-white z-20 w-full p-10 left-0 right-0'
          style={{
            boxShadow: '0 -2px 4px 0 rgb(0 0 0 / 10%), 0 0 20px 0 rgb(0 0 0 / 10%)',
          }}
        >
          <div className='font-bold text-center text-lg mb-10'>
            <span>{t('estimatedTotal', 'Estimated Total')}:&nbsp;</span>
            <span className='relative'>
              <Loader
                className='spinner spinner--xs'
                type='ThreeDots'
                color='#333'
                visible={loading}
                height={100}
                width={100}
              />
              <span className={loading ? 'opacity-0' : ''}>
                <FormattedPrice currency={currency} amount={itemSubtotal - creditsApplied} />
              </span>
            </span>
          </div>
          <div className='text-gray text-sm flex justify-center mt-5'>
            <AfterPayInfo
              minPrice={itemSubtotal - creditsApplied}
              maxPrice={itemSubtotal - creditsApplied}
              currency={currency}
            />
          </div>
          <div className='pt-10'>
            <Button
              component={Link}
              to={CHECKOUT_URL}
              color='secondary'
              variant='contained'
              size='large'
              fullWidth
              disableElevation
            >
              <span className='font-bold'>{t('button.proceedToCheckout', 'Proceed to Checkout')}</span>
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

export default CartLinesTotal
